// src/components/Modals/QcDelayReasonDialog.tsx
//
// EE-216 - the prompt in front of closing the last QC step of a production
// line that is already past its Production Deadline. Records the reason it
// slipped, any contributing ones, and a comment that becomes mandatory for
// "Other".
//
// The ticket's explanatory paragraph ("This information helps us identify
// recurring causes and improve production planning") is deliberately not here:
// the box interrupts someone closing a step, who needs the question, not the
// rationale. The fields themselves are the ticket's.
//
// Structured on RequisitionPromptDialog (the requisition Reject/Close/Cancel
// prompt), which solved the same shape - a reason select whose choice decides
// whether the comment is required - and is the most current modal in the repo.
//
// Both reason fields are the same control, a select, differing only in
// `multiple`. Contributing reasons were a row of toggle chips first; with
// twelve reasons that row wrapped to three lines and pushed the comment box
// off the bottom of the dialog, and it read as a different kind of input than
// the field directly above it asking almost the same question.
//
// The reason list is never hardcoded here. It comes from the DELAY_REASONS
// table via useDelayReasons - the same list the order completion delay and the
// Production Deadline change use.
import { FunctionComponent, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useDelayReasons } from "hooks/useDelayReasons";

export type QcDelaySubmission = {
  delayPrimaryReason: string;
  delayContributingReasons: string[];
  delayComment?: string;
};

type PropsType = {
  open: boolean;
  /** The production's deadline, shown in the message so the user sees what was missed. */
  deadline?: string | null;
  /** The part (product id) the late step belongs to, as the row labels it. */
  partId?: number;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (submission: QcDelaySubmission) => void;
};

const QcDelayReasonDialog: FunctionComponent<PropsType> = ({
  open,
  deadline,
  partId,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const {
    selectableOptions: options,
    isLoading,
    isUnavailable,
    labelFor,
  } = useDelayReasons();
  const [primary, setPrimary] = useState("");
  const [contributing, setContributing] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  // Seeded every time it opens, as DeadlineChangeDialog does, so a second
  // late step never shows the previous one's answer.
  useEffect(() => {
    if (!open) {
      return;
    }
    setPrimary("");
    setContributing([]);
    setComment("");
    setShowErrors(false);
  }, [open]);

  const selected = options.find((option) => option.code === primary);
  const needsComment = !!selected?.requiresComment;

  const primaryMissing = showErrors && !primary;
  const commentMissing = showErrors && needsComment && !comment.trim();

  // The primary reason is not a contributing one as well - the backend rejects
  // the duplicate, and it would double-count in any later aggregation.
  const contributingOptions = options.filter(
    (option) => option.code !== primary
  );

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!primary || (needsComment && !comment.trim())) {
      setShowErrors(true);
      return;
    }
    onConfirm({
      delayPrimaryReason: primary,
      delayContributingReasons: contributing,
      delayComment: comment.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reason for QC delay</DialogTitle>
      <DialogContent>
        {partId !== undefined && (
          <DialogContentText>
            Applies to: <strong>Part #{partId}</strong>
          </DialogContentText>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          Production deadline:{" "}
          <strong>
            {/* A deadline is a day: sliced, not parsed, so a UTC midnight
                never shows as the previous day. Same format as
                DeadlineChangeDialog. */}
            {deadline
              ? dayjs(deadline.slice(0, 10)).format("D MMM YYYY")
              : "unknown"}
          </strong>
          .
        </DialogContentText>

        {isUnavailable && (
          <DialogContentText sx={{ mb: 2 }} color="error">
            The delay reasons could not be loaded, so the step cannot be
            completed. Reload the page, and tell an administrator if it keeps
            happening.
          </DialogContentText>
        )}
        <TextField
          select
          fullWidth
          required
          sx={{ mb: 2 }}
          label="Reason for delay"
          value={primary}
          onChange={(e) => {
            const code = e.target.value;
            setPrimary(code);
            setContributing((prev) => prev.filter((c) => c !== code));
            if (showErrors) setShowErrors(false);
          }}
          error={primaryMissing}
          helperText={primaryMissing ? "Required" : undefined}
        >
          <MenuItem value="">Select...</MenuItem>
          {options.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          sx={{ mb: 2 }}
          label="Also contributed (optional)"
          value={contributing}
          onChange={(e) => {
            // With `multiple`, MUI hands back the whole selection as an array,
            // which the DOM event type does not describe.
            const codes = e.target.value as unknown as string[];
            setContributing(codes);
          }}
          SelectProps={{
            multiple: true,
            // The default joins the raw codes; these are the backend's labels.
            renderValue: (value) => (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                {(value as string[]).map((code) => (
                  <Chip key={code} label={labelFor(code)} size="small" />
                ))}
              </div>
            ),
          }}
        >
          {contributingOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              <Checkbox
                size="small"
                checked={contributing.includes(option.code)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          minRows={2}
          label={needsComment ? "Comment (required)" : "Comment (optional)"}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (showErrors) setShowErrors(false);
          }}
          error={commentMissing}
          helperText={commentMissing ? "Required for this reason" : undefined}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={isSubmitting || isLoading || isUnavailable}
        >
          {isSubmitting ? <CircularProgress size={18} /> : "Complete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QcDelayReasonDialog;
